import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, subDays, isValid, isToday } from 'date-fns';
import { enUS, vi as viLocale } from 'date-fns/locale';
import {
  History, Search, AlertTriangle, Clock, Pause, Play, CheckCircle2,
  RotateCcw, MessageSquare, ArrowRightLeft, Edit3, Users, MapPin,
  RefreshCw, Info, ChevronDown, ChevronUp, Package,
  CircleDot, XCircle, Lightbulb, Radio,
} from 'lucide-react';
import { useProductionReplay } from '../../hooks/useProductionReplay';
import { useLabourCostCalculation } from '../../hooks/useLabourCostCalculation';
import { useAllProductionAreas, useAdminSettings, useTaskCategories, useTaskTags, useFloorWorkers, useFloorWorkerRates } from '../../hooks/useCompanyData';
import { useBestRunBenchmarks } from '../../hooks/useBestRunBenchmarks';
import { useTimer } from '../../hooks/useTimer';
import ExportButton from '../../components/common/ExportButton';
import {
  EVENT_TYPE_I18N,
  eventGroupForType,
  formatSummaryMetric,
  buildReplayCSV,
  REPLAY_EVENT_TYPE_OPTIONS,
  parseTaskEditChanges,
  TASK_EDIT_FIELD_I18N,
  humanizeFieldKey,
  getEventCostAndUnits,
} from '../../utils/productionReplay';
import { findNowMarkerIndex } from '../../utils/replayLive';
import { PRODUCTION_EVENT_TYPES } from '../../utils/productionEvents';
import { formatTime, formatElapsed } from '../../utils/time';
import { formatDuration, formatCurrency, calcLabourCost } from '../../utils/labour';
import { buildCSV, downloadCSV, makeExportFilename } from '../../utils/csv';
import './ProductionReplayPage.css';

const DATE_PRESETS = ['today', 'yesterday'];
const DEFAULT_FILTERS = {
  search: '',
  taskType: 'all',
  workerName: '',
  roomId: '',
  taskCategoryId: '',
  tagId: '',
  productName: '',
  eventGroup: '',
  eventType: '',
  statusScope: '',
  highCost: false,
  delayed: false,
  incomplete: false,
  longRunsOnly: false,
  needsAttention: false,
  notesOnly: false,
};

function parseDateParam(param) {
  if (!param) return new Date();
  const match = param.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    const local = new Date(Number(y), Number(m) - 1, Number(d));
    return isValid(local) ? local : new Date();
  }
  return new Date();
}

function normalizeMetaText(value) {
  return (value || '').trim().toLowerCase();
}

/** One readable review line from live-task badges (avoids badge clutter). */
function buildReviewSummary(liveReview, { isPaused } = {}) {
  if (!liveReview?.needsAttention && !isPaused) return null;
  const warnings = liveReview.badges?.filter((b) => b.tone === 'warning') ?? [];
  if (isPaused) {
    return warnings.length ? warnings.map((w) => w.detail || w.label).join(' ') : null;
  }
  if (!warnings.length) return null;
  return warnings.map((w) => w.detail || w.label).join(' ');
}

/** Shorter review line for live sidebar (elapsed shown separately). */
function buildLiveReviewHint(liveReview, { isPaused } = {}) {
  if (!liveReview?.needsAttention) return null;
  const warnings = liveReview.badges?.filter((b) => b.tone === 'warning') ?? [];
  if (!warnings.length) return null;

  if (isPaused) {
    return warnings.map((w) => w.detail || w.label).join(' ');
  }

  const primary = warnings.find((w) => w.key === 'running_longer')
    || warnings.find((w) => w.key === 'may_need_review')
    || warnings.find((w) => w.key === 'long_elapsed')
    || warnings[0];
  return primary.detail || primary.label;
}

function buildEventMetaItems({
  event, workers, units, cost, currency, taskTitle, t,
}) {
  const seen = new Set([normalizeMetaText(taskTitle)]);

  const add = (key, text, icon) => {
    if (!text) return null;
    const normalized = normalizeMetaText(text);
    if (!normalized || seen.has(normalized)) return null;
    seen.add(normalized);
    return { key, icon, text };
  };

  return [
    add('product', event.productName, Package),
    add('category', event.taskCategoryName, null),
    event.tagNames?.length
      ? add('tags', event.tagNames.join(', '), null)
      : null,
    add('worker', workers, Users),
    add('room', event.roomName, MapPin),
    units != null && units > 0 ? add('units', t('replayExtra.unitsCount', { count: units }), Package) : null,
    cost != null && cost > 0 ? add('cost', formatCurrency(cost, currency), null) : null,
  ].filter(Boolean);
}

function EventTypeIcon({ eventType, size = 13 }) {
  switch (eventType) {
    case PRODUCTION_EVENT_TYPES.TASK_STARTED:
      return <Play size={size} />;
    case PRODUCTION_EVENT_TYPES.TASK_COMPLETED:
      return <CheckCircle2 size={size} />;
    case PRODUCTION_EVENT_TYPES.TASK_PAUSED:
      return <Pause size={size} />;
    case PRODUCTION_EVENT_TYPES.TASK_RESUMED:
      return <RotateCcw size={size} />;
    case PRODUCTION_EVENT_TYPES.NOTE_ADDED:
      return <MessageSquare size={size} />;
    case PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED:
      return <AlertTriangle size={size} />;
    case PRODUCTION_EVENT_TYPES.HANDOFF:
    case PRODUCTION_EVENT_TYPES.WORKER_ADDED:
    case PRODUCTION_EVENT_TYPES.WORKER_REMOVED:
      return <ArrowRightLeft size={size} />;
    case PRODUCTION_EVENT_TYPES.ROOM_CHANGED:
    case PRODUCTION_EVENT_TYPES.TASK_EDITED:
    case PRODUCTION_EVENT_TYPES.QUANTITY_UPDATED:
      return <Edit3 size={size} />;
    case PRODUCTION_EVENT_TYPES.TASK_CANCELLED:
      return <XCircle size={size} />;
    default:
      return <CircleDot size={size} />;
  }
}

function MetricItem({ label, value, sub, highlight, valueTitle }) {
  const title = valueTitle ?? (typeof value === 'string' ? value : undefined);
  return (
    <div className={`replay-metric${highlight ? ' replay-metric--highlight' : ''}`}>
      <span className="replay-metric-label">{label}</span>
      <span className="replay-metric-value" title={title}>
        {value}
      </span>
      {sub && <span className="replay-metric-sub" title={typeof sub === 'string' ? sub : undefined}>{sub}</span>}
    </div>
  );
}

function MetricGroup({ title, children }) {
  return (
    <div className="replay-metric-group">
      <h3 className="replay-metric-group-title">{title}</h3>
      <div className="replay-metric-group-items">{children}</div>
    </div>
  );
}

function DaySummaryPanel({ summary, currency, t }) {
  return (
    <div className="replay-day-summary">
      <h2 className="replay-day-summary-title">{t('replay.daySummary')}</h2>
      <p className="replay-narrative">{summary.narrative}</p>

      <MetricGroup title={t('replay.groups.activity')}>
        <MetricItem
          label={t('replay.stats.started')}
          value={summary.tasksStarted}
          highlight={summary.tasksStarted > 0}
        />
        <MetricItem
          label={t('replay.stats.completed')}
          value={summary.tasksCompleted}
          highlight={summary.tasksCompleted > 0}
        />
        {summary.incompleteActive > 0 && (
          <MetricItem
            label={t('replay.stats.running')}
            value={summary.incompleteActive}
            highlight
          />
        )}
      </MetricGroup>

      <MetricGroup title={t('replay.groups.time')}>
        <MetricItem
          label={t('replay.stats.activeTime')}
          value={formatSummaryMetric(summary.activeMinutes, 'duration')}
        />
        <MetricItem
          label={t('replay.stats.pauseTime')}
          value={formatSummaryMetric(summary.pauseMinutes, 'duration')}
        />
        {summary.longestTask && (
          <MetricItem
            label={t('replay.stats.longestTask')}
            value={summary.longestTask.name}
            valueTitle={summary.longestTask.name}
            sub={formatDuration(summary.longestTask.minutes)}
          />
        )}
        {summary.biggestDelay && summary.biggestDelay.minutes > 0 && (
          <MetricItem
            label={t('replay.stats.biggestDelay')}
            value={formatDuration(summary.biggestDelay.minutes)}
            sub={summary.biggestDelay.name}
          />
        )}
      </MetricGroup>

      {(summary.units > 0 || summary.labourCost > 0) && (
        <MetricGroup title={t('replay.groups.output')}>
          {summary.units > 0 && (
            <MetricItem label={t('replay.stats.units')} value={summary.units} highlight />
          )}
          {summary.labourCost > 0 && (
            <MetricItem
              label={t('replay.stats.labourCost')}
              value={formatSummaryMetric(summary.labourCost, 'cost', currency)}
            />
          )}
          {summary.avgCostPerUnit != null && (
            <MetricItem
              label={t('replay.stats.costPerUnit')}
              value={formatCurrency(summary.avgCostPerUnit, currency)}
            />
          )}
        </MetricGroup>
      )}

      {(summary.mostActiveWorker || summary.mostActiveRoom) && (
        <MetricGroup title={t('replay.groups.people')}>
          {summary.mostActiveWorker && (
            <MetricItem
              label={t('replay.stats.mostActiveWorker')}
              value={summary.mostActiveWorker.name}
              valueTitle={summary.mostActiveWorker.name}
              sub={formatDuration(summary.mostActiveWorker.minutes)}
            />
          )}
          {summary.mostActiveRoom && (
            <MetricItem
              label={t('replay.stats.mostActiveRoom')}
              value={summary.mostActiveRoom.name}
              valueTitle={summary.mostActiveRoom.name}
              sub={formatDuration(summary.mostActiveRoom.minutes)}
            />
          )}
        </MetricGroup>
      )}
    </div>
  );
}

function LiveTaskCard({ liveTask, userRates, defaultTeamRate, currency, t }) {
  const task = liveTask.task;
  const isPaused = liveTask.paused;
  const prevPausedSeconds = (task.pausedMinutes ?? 0) * 60;
  const startRaw = task.startTime?.toDate?.() ?? task.startTime;

  const timerElapsed = useTimer(startRaw, !isPaused, prevPausedSeconds);

  const frozenSeconds = useMemo(() => {
    if (!isPaused || !task.pausedAt || !startRaw) return 0;
    const pausedAt = task.pausedAt.toDate ? task.pausedAt.toDate() : new Date(task.pausedAt);
    const start = startRaw instanceof Date ? startRaw : new Date(startRaw);
    return Math.max(0, Math.floor((pausedAt - start) / 1000) - prevPausedSeconds);
  }, [isPaused, task.pausedAt, startRaw, prevPausedSeconds]);

  const elapsedSec = isPaused ? frozenSeconds : timerElapsed;
  const elapsedMin = Math.max(0, Math.round(elapsedSec / 60));

  const liveCost = useMemo(() => {
    if (elapsedMin <= 0) return null;
    const { cost } = calcLabourCost(
      elapsedMin,
      task.employeeIds,
      task.teamSize,
      userRates,
      defaultTeamRate,
    );
    return cost;
  }, [elapsedMin, task.employeeIds, task.teamSize, userRates, defaultTeamRate]);

  const reviewHint = buildLiveReviewHint(liveTask, { isPaused });

  return (
    <li className={`replay-live-item${liveTask.needsAttention ? ' replay-live-item--review' : ''}`}>
      <div className="replay-live-item-header">
        <span className="replay-live-item-name">{liveTask.name}</span>
        <span className={`replay-live-status replay-live-status--${isPaused ? 'paused' : 'running'}`}>
          {isPaused ? t('replay.paused') : t('replay.running')}
        </span>
      </div>
      {(liveTask.worker || liveTask.room) && (
        <div className="replay-live-item-meta">
          {liveTask.worker && (
            <span><Users size={12} aria-hidden="true" /> {liveTask.worker}</span>
          )}
          {liveTask.room && (
            <span><MapPin size={12} aria-hidden="true" /> {liveTask.room}</span>
          )}
        </div>
      )}
      <div className="replay-live-item-stats">
        <span className="replay-live-elapsed" aria-live="polite">{formatElapsed(elapsedSec)}</span>
        {liveCost != null && (
          <span className="replay-live-cost">
            {formatCurrency(liveCost, currency)}
            <span className="replay-live-est">{t('replay.estimatedCostSoFar')}</span>
          </span>
        )}
        {liveTask.unitsCompleted != null && liveTask.unitsCompleted > 0 && (
          <span className="replay-live-units">
            <Package size={12} aria-hidden="true" /> {liveTask.unitsCompleted} {t('replay.unitsDone')}
          </span>
        )}
      </div>
      {liveTask.benchmarkMinutes && !reviewHint && (
        <p className="replay-live-benchmark">
          {t('replay.usualTime', { time: formatDuration(liveTask.benchmarkMinutes) })}
        </p>
      )}
      {reviewHint && (
        <p className="replay-live-review">
          <AlertTriangle size={13} aria-hidden="true" />
          {reviewHint}
        </p>
      )}
    </li>
  );
}

function LiveTasksPanel({ liveTasks, userRates, defaultTeamRate, currency, t }) {
  if (!liveTasks.length) return null;

  const reviewCount = liveTasks.filter((lt) => lt.needsAttention).length;

  return (
    <div className="replay-live-panel">
      <div className="replay-live-header">
        <Radio size={16} className="replay-live-icon" aria-hidden="true" />
        <h3>{reviewCount > 0 ? t('replay.needsAttention') : t('replay.liveTasks')}</h3>
        <span className="replay-live-count">{liveTasks.length}</span>
      </div>
      <ul className="replay-live-list">
        {liveTasks.map((lt) => (
          <LiveTaskCard
            key={lt.id}
            liveTask={lt}
            userRates={userRates}
            defaultTeamRate={defaultTeamRate}
            currency={currency}
            t={t}
          />
        ))}
      </ul>
    </div>
  );
}

function ReplayNowMarker({ isLive }) {
  const { t } = useTranslation();
  return (
    <div className={`replay-now-marker${isLive ? ' replay-now-marker--live' : ''}`} aria-label={t('a11y.currentTime')}>
      <div className="replay-now-rail">
        <span className="replay-now-label">{t('replayExtra.now')}</span>
        <span className="replay-now-dot" aria-hidden="true" />
      </div>
      <div className="replay-now-line">
        <time dateTime={new Date().toISOString()}>{formatTime(new Date())}</time>
      </div>
    </div>
  );
}

function ReplayInsightsPanel({ insights, t }) {
  if (!insights.length) return null;

  return (
    <div className="replay-insights-panel">
      <div className="replay-insights-header">
        <Lightbulb size={16} />
        <h3>{t('replay.replayInsights')}</h3>
      </div>
      <ul className="replay-insights-list">
        {insights.map((item) => (
          <li key={item.id}>{item.message}</li>
        ))}
      </ul>
    </div>
  );
}

function TimelineEvent({
  event, showGapAfter, gap, taskStatus, task, currency, isLastActiveEvent, liveReview,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const group = eventGroupForType(event.eventType);
  const labelKey = EVENT_TYPE_I18N[event.eventType];
  const label = labelKey ? t(labelKey) : event.eventType;
  const editChanges = useMemo(
    () => parseTaskEditChanges(event, {
      currency,
      labelForField: (key) => {
        const i18nKey = TASK_EDIT_FIELD_I18N[key];
        return i18nKey ? t(i18nKey) : humanizeFieldKey(key);
      },
    }),
    [event, currency, t],
  );
  const workers = event.employeeNames?.length
    ? event.employeeNames.join(', ')
    : event.teamSize > 1 ? t('replayExtra.teamX', { size: event.teamSize }) : null;
  const isRunning = taskStatus === 'active' && isLastActiveEvent;
  const isPausedLive = isRunning && Boolean(task?.pausedAt);

  const prevPausedSeconds = (task?.pausedMinutes ?? 0) * 60;
  const startRaw = task?.startTime?.toDate?.() ?? task?.startTime;
  const timerElapsed = useTimer(startRaw, isRunning && !isPausedLive, prevPausedSeconds);

  const frozenSeconds = useMemo(() => {
    if (!isPausedLive || !task?.pausedAt || !startRaw) return 0;
    const pausedAt = task.pausedAt.toDate ? task.pausedAt.toDate() : new Date(task.pausedAt);
    const start = startRaw instanceof Date ? startRaw : new Date(startRaw);
    return Math.max(0, Math.floor((pausedAt - start) / 1000) - prevPausedSeconds);
  }, [isPausedLive, task?.pausedAt, startRaw, prevPausedSeconds]);

  const liveElapsedSec = isRunning ? (isPausedLive ? frozenSeconds : timerElapsed) : 0;

  const { units, cost } = getEventCostAndUnits(event, task);
  const isLongPause = (event.durationMinutes ?? 0) >= 30
    && event.eventType === PRODUCTION_EVENT_TYPES.TASK_PAUSED
    && !event.metadata?.syntheticPauseSummary;
  const isDelay = event.eventType === PRODUCTION_EVENT_TYPES.MATERIAL_DELAY_LOGGED;
  const hasComplexDetails = Boolean(
    editChanges.length
    || (event.reason && event.reason !== event.notes)
    || (event.previousValue && event.newValue && event.eventType === PRODUCTION_EVENT_TYPES.ROOM_CHANGED),
  );
  const hasNotes = Boolean(event.notes);
  const showNotesInline = hasNotes && !hasComplexDetails && (event.notes?.length ?? 0) <= 120;
  const hasExpandableDetails = hasComplexDetails || (hasNotes && !showNotesInline);
  const durationLabel = event.metadata?.syntheticPauseSummary
    ? t('replayExtra.downtimePrefix', { duration: formatDuration(event.durationMinutes) })
    : formatDuration(event.durationMinutes);

  const needsReview = isRunning && liveReview?.needsAttention;
  const reviewSummary = isRunning
    ? buildReviewSummary(liveReview, { isPaused: isPausedLive })
    : null;

  const taskTitle = event.taskName || task?.taskName || t('replayExtra.unknownTask');
  const metaItems = buildEventMetaItems({
    event,
    workers,
    units,
    cost,
    currency,
    taskTitle,
    t,
  });

  const eventClasses = [
    'replay-event',
    `replay-event--${group}`,
    event.highlight === 'long-running' ? 'replay-event--long-run' : '',
    isDelay ? 'replay-event--highlight-delay' : '',
    isLongPause ? 'replay-event--long-pause' : '',
    isRunning ? 'replay-event--running' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <article className={eventClasses} data-event-id={event.id}>
        <div className="replay-event-rail">
          <time className="replay-event-time">{formatTime(event.timestamp)}</time>
          <div className={`replay-event-marker replay-event-marker--${group}`} aria-hidden="true">
            <EventTypeIcon eventType={event.eventType} />
          </div>
        </div>

        <div className="replay-event-card">
          <header className="replay-event-header">
            <div className="replay-event-header-main">
              <span className={`replay-event-badge replay-event-badge--${group}`}>{label}</span>
              {event.synthetic && (
                <span className="replay-event-est" title={t('replayExtra.reconstructedTitle')}>
                  {t('replayExtra.estimated')}
                </span>
              )}
            </div>
            {isRunning && liveElapsedSec > 0 && !needsReview ? (
              <span className="replay-event-duration-badge replay-event-duration-badge--live">
                <Clock size={12} aria-hidden="true" />
                <span className="replay-event-duration-value">{formatElapsed(liveElapsedSec)}</span>
              </span>
            ) : !isRunning && event.durationMinutes != null && event.durationMinutes > 0 && (
              <span className="replay-event-duration-badge">
                <Clock size={12} aria-hidden="true" />
                {durationLabel}
              </span>
            )}
          </header>

          <h4 className="replay-event-task">{taskTitle}</h4>

          {metaItems.length > 0 && (
            <ul className="replay-event-meta">
              {metaItems.map((item) => (
                <li key={item.key} className="replay-event-meta-item">
                  {item.icon && <item.icon size={13} aria-hidden="true" />}
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          )}

          {showNotesInline && (
            <p className="replay-event-notes">{event.notes}</p>
          )}

          {reviewSummary && (
            <p className="replay-event-live-hint">{reviewSummary}</p>
          )}

          {hasExpandableDetails && (
            <div className="replay-event-details">
              {!expanded ? (
                <button
                  type="button"
                  className="replay-event-expand"
                  aria-expanded="false"
                  onClick={() => setExpanded(true)}
                >
                  {t('replayExtra.viewDetails')} <ChevronDown size={14} aria-hidden="true" />
                </button>
              ) : (
                <>
                  {hasNotes && <p className="replay-event-notes">{event.notes}</p>}
                  {event.reason && event.reason !== event.notes && (
                    <p className="replay-event-reason">{event.reason}</p>
                  )}
                  {editChanges.length > 0 && (
                    <ul className="replay-event-edits">
                      {editChanges.map((change) => (
                        <li key={change.field} className="replay-event-edit-row">
                          <span className="replay-event-edit-label">{change.label}</span>
                          <span className="replay-event-edit-values">
                            <span className="replay-event-edit-from">{change.before}</span>
                            <span className="replay-event-edit-arrow" aria-hidden="true">→</span>
                            <span className="replay-event-edit-to">{change.after}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {!editChanges.length && event.previousValue && event.newValue
                    && event.eventType === PRODUCTION_EVENT_TYPES.ROOM_CHANGED && (
                    <p className="replay-event-edit">
                      {event.previousValue} → {event.newValue}
                    </p>
                  )}
                  <button
                    type="button"
                    className="replay-event-expand"
                    aria-expanded="true"
                    onClick={() => setExpanded(false)}
                  >
                    {t('replayExtra.hideDetails')} <ChevronUp size={14} aria-hidden="true" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </article>

      {showGapAfter && gap && (
        <div className="replay-gap">
          <Pause size={14} aria-hidden="true" />
          <div className="replay-gap-text">
            <strong>{t('replayExtra.gapNoActivity', { duration: formatDuration(gap.minutes) })}</strong>
            <span>
              {t('replayExtra.gapTimeRange', {
                start: formatTime(gap.start),
                end: formatTime(gap.end),
              })}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

export default function ProductionReplayPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminSettings } = useAdminSettings();
  const { areas } = useAllProductionAreas();
  const { categories } = useTaskCategories();
  const { tags } = useTaskTags();
  const { workers } = useFloorWorkers();
  const { rates: workerRates } = useFloorWorkerRates();
  const currency = adminSettings?.labourCostCurrency || 'CAD';
  const defaultTeamRate = adminSettings?.defaultTeamHourlyRate ?? null;

  const userRates = useMemo(() => {
    const m = {};
    for (const [id, rateData] of Object.entries(workerRates)) {
      if (rateData.hourlyRate != null) m[id] = rateData.hourlyRate;
    }
    return m;
  }, [workerRates]);

  const labourContext = useMemo(
    () => ({ userRates, defaultTeamRate }),
    [userRates, defaultTeamRate],
  );

  const { runCalculation } = useLabourCostCalculation();

  useEffect(() => { runCalculation(); }, [runCalculation]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedDate = useMemo(
    () => parseDateParam(searchParams.get('date')),
    [searchParams],
  );

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  const { benchmarks } = useBestRunBenchmarks({ enabled: true });

  const replayFilters = useMemo(
    () => ({ ...filters, currency }),
    [filters, currency],
  );

  const {
    events, tasks, summary, insights, liveTasks, isLiveMode, loading, error, eventsUnavailable,
  } = useProductionReplay(selectedDate, replayFilters, benchmarks, labourContext);

  const tasksById = useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks],
  );

  const gapMap = useMemo(() => {
    const map = new Map();
    for (const gap of insights.gaps) {
      if (gap.afterEvent?.id) map.set(gap.afterEvent.id, gap);
    }
    return map;
  }, [insights.gaps]);

  const lastActiveEventId = useMemo(() => {
    const byTask = new Map();
    for (const ev of events) {
      const task = tasksById.get(ev.taskId);
      if (task?.status === 'active') byTask.set(ev.taskId, ev.id);
    }
    return new Set(byTask.values());
  }, [events, tasksById]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.workerName) count += 1;
    if (filters.roomId) count += 1;
    if (filters.taskCategoryId) count += 1;
    if (filters.tagId) count += 1;
    if (filters.productName) count += 1;
    if (filters.eventGroup) count += 1;
    if (filters.taskType !== 'all') count += 1;
    if (filters.statusScope) count += 1;
    if (filters.highCost) count += 1;
    if (filters.delayed) count += 1;
    if (filters.incomplete) count += 1;
    if (filters.longRunsOnly) count += 1;
    if (filters.needsAttention) count += 1;
    if (filters.notesOnly) count += 1;
    return count;
  }, [filters]);

  const showNowMarker = isToday(selectedDate) && !loading && !error;
  const nowMarkerIndex = useMemo(
    () => (showNowMarker ? findNowMarkerIndex(events).insertAfter : -1),
    [showNowMarker, events],
  );

  const setDate = (date) => setSearchParams({ date: format(date, 'yyyy-MM-dd') });

  const handleDateChange = (e) => {
    if (e.target.value) setDate(parseDateParam(e.target.value));
  };

  const handlePreset = (preset) => {
    const d = preset === 'today' ? new Date() : subDays(new Date(), 1);
    setDate(d);
  };

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const clearFilters = () => setFilters({ ...DEFAULT_FILTERS });

  const handleExport = useCallback(() => {
    const { headers, rows } = buildReplayCSV(events);
    const csv = buildCSV(headers, rows);
    downloadCSV(csv, makeExportFilename('production-replay', format(selectedDate, 'yyyy-MM-dd')));
  }, [events, selectedDate]);

  const workerOptions = useMemo(() => {
    const names = new Set();
    for (const w of workers) names.add(w.name);
    for (const task of tasks) {
      for (const n of task.employeeNames || []) names.add(n);
    }
    return [...names].sort();
  }, [workers, tasks]);

  const productOptions = useMemo(() => {
    const names = new Set();
    for (const task of tasks) {
      if (task.productName) names.add(task.productName);
    }
    return [...names].sort();
  }, [tasks]);

  const activePreset = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const yesterdayStr = format(subDays(new Date(), 1), 'yyyy-MM-dd');
    const cur = format(selectedDate, 'yyyy-MM-dd');
    if (cur === todayStr) return 'today';
    if (cur === yesterdayStr) return 'yesterday';
    return '';
  }, [selectedDate]);

  const dateLocale = i18n.language === 'vi' ? viLocale : enUS;
  const showSummary = !loading && !error;

  return (
    <div className="replay-page">
      <div className="page-header">
        <div>
          <h1>{t('replay.title')}</h1>
          <p className="page-header-sub">{t('replay.subtitle')}</p>
        </div>
      </div>

      <div className="page-content-wide replay-page-content">
        {eventsUnavailable && !loading && (
          <div className="replay-banner replay-banner--info">
            <Info size={16} aria-hidden="true" />
            {t('replay.eventsUnavailable')}
          </div>
        )}

        <div className="replay-layout">
          <div className="card replay-controls-card">
            <div className="replay-controls">
              <div className="replay-controls-primary">
                <div className="search-box replay-search">
                  <Search size={15} aria-hidden="true" />
                  <input
                    className="search-input"
                    type="search"
                    placeholder={t('replay.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilter('search', e.target.value)}
                    aria-label={t('replay.searchPlaceholder')}
                  />
                </div>

                <div className="replay-date-controls">
                  <div className="filter-segment" role="group" aria-label={t('a11y.dateRange')}>
                    {DATE_PRESETS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`filter-seg-btn${activePreset === p ? ' active' : ''}`}
                        onClick={() => handlePreset(p)}
                      >
                        {t(`common.${p}`)}
                      </button>
                    ))}
                  </div>
                  <input
                    type="date"
                    className="replay-date-input"
                    value={format(selectedDate, 'yyyy-MM-dd')}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    onChange={handleDateChange}
                    aria-label={t('a11y.selectDate')}
                  />
                </div>
              </div>

              <div className="replay-controls-secondary">
                <button
                  type="button"
                  className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-ghost'} replay-filters-btn`}
                  aria-expanded={showFilters}
                  onClick={() => setShowFilters((v) => !v)}
                >
                  {t('replay.filters.toggle')}
                  {activeFilterCount > 0 && (
                    <span className="replay-filter-count" aria-label={t('replayExtra.activeFilters', { count: activeFilterCount })}>
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <div className="replay-export">
                  <ExportButton
                    options={[{
                      id: 'replay',
                      label: t('replay.export'),
                      description: events.length > 0
                        ? t('replay.eventCount', { count: events.length })
                        : t('replay.empty'),
                      disabled: events.length === 0,
                      onExport: handleExport,
                    }]}
                  />
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="replay-filters-panel">
                <div className="replay-filters-grid">
                  <label>
                    <span>{t('replay.filters.worker')}</span>
                    <select value={filters.workerName} onChange={(e) => setFilter('workerName', e.target.value)}>
                      <option value="">{t('replay.filters.all')}</option>
                      {workerOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>{t('replay.filters.room')}</span>
                    <select value={filters.roomId} onChange={(e) => setFilter('roomId', e.target.value)}>
                      <option value="">{t('replay.filters.all')}</option>
                      {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>{t('replay.filters.category')}</span>
                    <select value={filters.taskCategoryId} onChange={(e) => setFilter('taskCategoryId', e.target.value)}>
                      <option value="">{t('replay.filters.all')}</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>{t('replay.filters.tag')}</span>
                    <select value={filters.tagId} onChange={(e) => setFilter('tagId', e.target.value)}>
                      <option value="">{t('replay.filters.all')}</option>
                      {tags.map((tg) => <option key={tg.id} value={tg.id}>{tg.name}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>{t('replay.filters.product')}</span>
                    <select value={filters.productName} onChange={(e) => setFilter('productName', e.target.value)}>
                      <option value="">{t('replay.filters.all')}</option>
                      {productOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>{t('replay.filters.eventGroup')}</span>
                    <select value={filters.eventGroup} onChange={(e) => setFilter('eventGroup', e.target.value)}>
                      {REPLAY_EVENT_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>{t('replay.filters.taskType')}</span>
                    <select value={filters.taskType} onChange={(e) => setFilter('taskType', e.target.value)}>
                      <option value="all">{t('replay.filters.all')}</option>
                      <option value="production">{t('categories.production')}</option>
                      <option value="support">{t('categories.support')}</option>
                      <option value="delay">{t('categories.delay')}</option>
                      <option value="other">{t('categories.other')}</option>
                    </select>
                  </label>
                  <label>
                    <span>{t('replay.filters.status')}</span>
                    <select value={filters.statusScope} onChange={(e) => setFilter('statusScope', e.target.value)}>
                      <option value="">{t('replay.filters.all')}</option>
                      <option value="completed">{t('replay.filters.completed')}</option>
                      <option value="active">{t('replay.filters.active')}</option>
                    </select>
                  </label>
                </div>
                <div className="replay-filter-chips">
                  <button
                    type="button"
                    className={`filter-chip ${filters.delayed ? 'active' : ''}`}
                    onClick={() => setFilter('delayed', !filters.delayed)}
                  >
                    {t('replay.filters.delayed')}
                  </button>
                  <button
                    type="button"
                    className={`filter-chip ${filters.longRunsOnly ? 'active' : ''}`}
                    onClick={() => setFilter('longRunsOnly', !filters.longRunsOnly)}
                  >
                    {t('replay.filters.longRuns')}
                  </button>
                  <button
                    type="button"
                    className={`filter-chip ${filters.incomplete ? 'active' : ''}`}
                    onClick={() => setFilter('incomplete', !filters.incomplete)}
                  >
                    {t('replay.filters.incomplete')}
                  </button>
                  {isLiveMode && (
                    <button
                      type="button"
                      className={`filter-chip ${filters.needsAttention ? 'active' : ''}`}
                      onClick={() => setFilter('needsAttention', !filters.needsAttention)}
                    >
                      {t('replay.filters.needsAttention')}
                    </button>
                  )}
                  <button
                    type="button"
                    className={`filter-chip ${filters.notesOnly ? 'active' : ''}`}
                    onClick={() => setFilter('notesOnly', !filters.notesOnly)}
                  >
                    {t('replay.filters.notesIssues')}
                  </button>
                  <button
                    type="button"
                    className={`filter-chip ${filters.highCost ? 'active' : ''}`}
                    onClick={() => setFilter('highCost', !filters.highCost)}
                  >
                    {t('replay.filters.highCost')}
                  </button>
                  {activeFilterCount > 0 && (
                    <button type="button" className="replay-clear-filters" onClick={clearFilters}>
                      {t('replay.filters.clear')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {showSummary && (
            <aside className="replay-sidebar" aria-label={t('a11y.daySummaryInsights')}>
              <div className="replay-sidebar-inner">
                {isLiveMode && liveTasks.length > 0 && (
                  <section className="replay-sidebar-section replay-sidebar-section--live" aria-label={t('replay.liveTasks')}>
                    <LiveTasksPanel
                      liveTasks={liveTasks}
                      userRates={userRates}
                      defaultTeamRate={defaultTeamRate}
                      currency={currency}
                      t={t}
                    />
                  </section>
                )}

                <section className="replay-sidebar-section replay-sidebar-section--summary" aria-label={t('replay.daySummary')}>
                  <DaySummaryPanel summary={summary} currency={currency} t={t} />
                </section>

                {insights.replayInsights?.length > 0 && (
                  <section className="replay-sidebar-section replay-sidebar-section--insights" aria-label={t('replay.replayInsights')}>
                    <ReplayInsightsPanel insights={insights.replayInsights} t={t} />
                  </section>
                )}
              </div>
            </aside>
          )}

          <div className="card replay-timeline-card">
            <div className="replay-timeline-header">
              <h2 className="replay-day-title">{format(selectedDate, 'EEEE, MMMM d, yyyy', { locale: dateLocale })}</h2>
              <div className="replay-timeline-header-end">
                {isLiveMode && (
                  <span className="replay-live-pill">
                    <CircleDot size={10} aria-hidden="true" /> {t('replay.productionInProgress')}
                  </span>
                )}
                <span className="replay-event-count text-muted text-sm">
                  {loading ? '…' : t('replay.eventCount', { count: events.length })}
                </span>
              </div>
            </div>

            {loading && (
              <div className="replay-loading" role="status">
                <span className="spinner" />
                <p>{t('replay.loading')}</p>
              </div>
            )}

            {error && (
              <div className="replay-error" role="alert">
                <AlertTriangle size={20} aria-hidden="true" />
                <p>{t('replay.error')}</p>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => window.location.reload()}>
                  <RefreshCw size={14} aria-hidden="true" /> {t('replay.retry')}
                </button>
              </div>
            )}

            {!loading && !error && events.length === 0 && (
              <div className="replay-empty">
                <History size={32} strokeWidth={1.5} aria-hidden="true" />
                <p>{activeFilterCount > 0 || filters.search ? t('replay.emptyFiltered') : t('replay.empty')}</p>
                <p className="replay-empty-sub text-muted text-sm">
                  {activeFilterCount > 0 || filters.search ? t('replay.emptyFilteredSub') : t('replay.emptySub')}
                </p>
                {(activeFilterCount > 0 || filters.search) && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={clearFilters}>
                    {t('replay.filters.clear')}
                  </button>
                )}
              </div>
            )}

            {!loading && !error && events.length > 0 && (
              <div className="replay-timeline">
                {showNowMarker && nowMarkerIndex === -1 && (
                  <ReplayNowMarker isLive={isLiveMode} />
                )}
                {events.map((ev, index) => {
                  const task = tasksById.get(ev.taskId);
                  return (
                    <div key={ev.id}>
                      <TimelineEvent
                        event={ev}
                        showGapAfter={gapMap.has(ev.id)}
                        gap={gapMap.get(ev.id)}
                        taskStatus={task?.status}
                        task={task}
                        currency={currency}
                        isLastActiveEvent={lastActiveEventId.has(ev.id)}
                        liveReview={ev.liveReview}
                      />
                      {showNowMarker && index === nowMarkerIndex && (
                        <ReplayNowMarker isLive={isLiveMode} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
